import { memo, useCallback, useState } from 'react';

import { Checkbox } from '@air/primitive-checkbox';
import { Label } from '@air/primitive-label';

import { WorkspaceAgentPanelConsentDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentDisplay';
import { WorkspaceAgentPanelConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentDock';
import {
  workspaceAgentConsentDockRememberCheckbox,
  workspaceAgentConsentDockRememberLabel,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { useWorkspaceAgentElicitationTracking } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentElicitationTracking';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type ToolConsentElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';
import {
  getToolConsentDockCopy,
  TOOL_CONSENT_ALLOW_FOR_THIS_THREAD_LABEL,
  TOOL_CONSENT_DENY_LABEL,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getToolConsentDockCopy';

export type PrivateWorkspaceAgentPanelToolConsentDockProps = {
  elicitation: ToolConsentElicitation;
  onCancel: () => void;
};

export const PrivateWorkspaceAgentPanelToolConsentDock = memo(
  ({ elicitation, onCancel }: PrivateWorkspaceAgentPanelToolConsentDockProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const copy = getToolConsentDockCopy(elicitation);
    const toolName = elicitation.payload.toolName;
    const [rememberForSession, setRememberForSession] = useState(() => session.getConsentRememberForSession(toolName));
    const trackChoice = useWorkspaceAgentElicitationTracking({
      createdAt: elicitation.createdAt,
      elicitationId: elicitation.id,
      elicitationType: 'tool_consent',
      toolName,
    });

    const handleApprove = useCallback(() => {
      trackChoice({ cta: 'approve', rememberForSession });
      // Commit the box's final state for this tool before responding — matches backend `sessionToolConsent`.
      session.setConsentRememberForSession(toolName, rememberForSession);
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'approved',
        ...(rememberForSession ? { rememberForSession } : {}),
      });
    }, [elicitation.id, rememberForSession, session, toolName, trackChoice]);

    const handleCancel = useCallback(() => {
      trackChoice({ cta: 'cancel', rememberForSession });
      onCancel();
    }, [onCancel, rememberForSession, trackChoice]);

    const handleRememberChange = useCallback((value: boolean | 'indeterminate') => {
      setRememberForSession(value === true);
    }, []);

    const rememberCheckboxId = `workspace-agent-consent-remember-${elicitation.id}`;

    return (
      <WorkspaceAgentPanelConsentDock
        approveLabel={copy.approveLabel}
        cancelLabel={TOOL_CONSENT_DENY_LABEL}
        data-testid="WORKSPACE_AGENT_CONSENT"
        footerLeading={
          <Label className={workspaceAgentConsentDockRememberLabel()} htmlFor={rememberCheckboxId}>
            <Checkbox
              checked={rememberForSession}
              className={workspaceAgentConsentDockRememberCheckbox()}
              data-testid="WORKSPACE_AGENT_CONSENT_REMEMBER"
              id={rememberCheckboxId}
              onCheckedChange={handleRememberChange}
              size="small"
            />
            <span className="truncate">{TOOL_CONSENT_ALLOW_FOR_THIS_THREAD_LABEL}</span>
          </Label>
        }
        onApprove={handleApprove}
        onCancel={handleCancel}
        title={copy.title}
      >
        <WorkspaceAgentPanelConsentDisplay
          instruction={copy.instruction}
          sample={copy.sample}
          summary={copy.summary}
          truncated={copy.truncated}
        />
      </WorkspaceAgentPanelConsentDock>
    );
  },
);

PrivateWorkspaceAgentPanelToolConsentDock.displayName = 'PrivateWorkspaceAgentPanelToolConsentDock';
